import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { createPopper } from '@popperjs/core';
import useKeyPress from '../../hooks/use-keypress';
import useDOMRoot from '../../hooks/use-dom-root';
import s from './dropdown.module.css';
import { context, useIsIos } from './utils';
import useOnClickOutside from '../../hooks/use-on-click-outside';
import { containsNode } from '../../utils/dom';
import keyCodes from '../../utils/keycodes';
import { adhApplyDirection, adpApplyDirection, dimensions, offset, overflow, silent, fit, applyHeight, noflip, } from './popper';
import { context as groupContext } from '../group/utils';
const keys = [keyCodes.escape, keyCodes.tab];
const IGNORE_GROUP_CONTEXT = { present: false };
const Launchpad = ({ root, children }) => {
    return root ? createPortal(children, root) : children;
};
const Content = ({ container = { current: null }, bounds = false, adjustable = true, padding = true, adaptivePlacement = true, adaptiveHeight = true, hideOnOverflow = true, flex = false, children, height, positioning = 'start', placement = 'body', dataTest: externalDataTest, scrollVisible, }) => {
    const content = useRef(null);
    const { opened, control, toggle, state, registerContent, dataTest: contextDataTest } = useContext(context);
    const root = useDOMRoot({ anchor: 'dropdown-launchpad', style: s.root });
    const popper = useRef(null);
    const [width, setWidth] = useState((control === null || control === void 0 ? void 0 : control.offsetWidth) || 0);
    useOnClickOutside([content], event => {
        if (!control) {
            return;
        }
        if (opened && !containsNode(control, event.target)) {
            toggle();
        }
    });
    useKeyPress(keys, () => {
        if (opened) {
            toggle();
        }
    });
    useEffect(() => {
        registerContent(content);
    }, [registerContent]);
    useEffect(() => {
        var _a;
        if (!opened || !control || state !== 'show') {
            return;
        }
        const boundary = container.current || document.body;
        const modifiers = [offset, overflow(boundary)];
        if (adaptivePlacement || adaptiveHeight) {
            modifiers.push(dimensions(boundary), fit);
        }
        if (!adaptivePlacement) {
            modifiers.push(noflip);
        }
        if (adaptivePlacement) {
            modifiers.push(adpApplyDirection(container.current, positioning));
        }
        if (adaptiveHeight) {
            modifiers.push(adhApplyDirection(positioning), applyHeight);
        }
        if (popper.current) {
            (_a = popper.current) === null || _a === void 0 ? void 0 : _a.destroy();
        }
        popper.current = createPopper(control, content.current, {
            placement: `bottom-${positioning}`,
            modifiers,
        });
    }, [opened, control, container, state, adaptivePlacement, adaptiveHeight, positioning]);
    useEffect(() => {
        var _a;
        if (opened) {
            return;
        }
        (_a = popper.current) === null || _a === void 0 ? void 0 : _a.setOptions({ modifiers: [...silent] });
    }, [opened]);
    useEffect(() => {
        if (!control) {
            return;
        }
        setWidth(control.offsetWidth);
        const observer = new MutationObserver(() => setWidth(control.offsetWidth));
        observer.observe(control, { childList: true, subtree: true, characterData: true });
        return () => {
            observer.disconnect();
        };
    }, [control]);
    const isIos = useIsIos();
    if (!control || state === 'init' || state === 'idle') {
        return null;
    }
    return (React.createElement(Launchpad, { root: placement === 'body' ? root : null },
        React.createElement("div", { ref: content, style: Object.assign(Object.assign(Object.assign({}, (adjustable && { minWidth: width })), (bounds && { maxWidth: width })), (height && { maxHeight: height })), className: classNames(s.content, flex && s.flex, padding && s.padding, hideOnOverflow && s.hideable, state === 'show' && s.visible, placement === 'local' && s.local, scrollVisible && !isIos && s.scrollVisible), "data-height": height, "data-test": contextDataTest ? `${contextDataTest}-content` : externalDataTest },
            React.createElement(groupContext.Provider, { value: IGNORE_GROUP_CONTEXT }, children))));
};
export default Content;
//# sourceMappingURL=content.js.map